import type { FC } from 'react'
import { Autocomplete, Box, Checkbox, IconButton, TextField, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { FFInput } from '~/shared/ui'
import { useEffect, useEffectEvent } from 'react'
import { ShiftException, ShiftExceptionReason, isShiftExceptionStartDateInPast } from './utils'
import { formatInZone, ISO_DATE_FORMAT, zonedDateTime } from '~/shared/lib/date'

interface ShiftExceptionEditorProps {
  shiftException: ShiftException
  isHistory: boolean
  overlapError?: boolean
  onChange: (shiftException: ShiftException) => void
  onDelete: (shiftException: ShiftException) => void
  onError: (error: boolean) => void
}

const EXCEPTION_REASONS: ShiftExceptionReason[] = [
  'Late Call out',
  'PTO',
  'Late PTO',
  'Meeting',
  'Offsite Business',
  'Other',
]

const SORTED_EXCEPTION_REASONS = [...EXCEPTION_REASONS].sort()

// The `parse*` pair turns a stored instant into the wall clock the inputs show;
// the `format*` pair turns wall clock the user typed back into a stored instant.
// Both directions are anchored to the clinic's zone, never the viewer's.
const UTC_INSTANT = `${ISO_DATE_FORMAT}'T'HH:mm:ss'Z'`

const parseDateTime = (dateTimeString: string) =>
  formatInZone(dateTimeString, `${ISO_DATE_FORMAT}'T'HH:mm`)

const formatDateTime = (dateTimeString: string) =>
  formatInZone(zonedDateTime(dateTimeString), UTC_INSTANT, 'UTC')

const parseDate = (dateTimeString: string) => formatInZone(dateTimeString, ISO_DATE_FORMAT)

const formatDate = (dateTimeString: string, isStart: boolean) =>
  formatInZone(zonedDateTime(dateTimeString + (isStart ? 'T00:00' : 'T23:59')), UTC_INSTANT, 'UTC')

// Each check's message for a row, or '' when it passes. An overlap is shown but does not make the
// row invalid on its own.
const exceptionValidation = (shiftException: ShiftException, hasOverlap: boolean) => {
  const { lower, upper } = shiftException.period
  const isStartInPast = !!lower && shiftException.id < 0 && isShiftExceptionStartDateInPast(lower)
  const isStartAfterEnd = lower > upper
  let dateErrorText = ''
  if (hasOverlap) dateErrorText = 'Overlaps with another exception'
  else if (isStartAfterEnd) dateErrorText = 'Start more than End'
  return {
    lowerEmptyErrorText: lower ? '' : 'Start empty',
    upperEmptyErrorText: upper ? '' : 'End empty',
    dateErrorText,
    pastTimeErrorText: isStartInPast ? 'Cannot schedule shift exception in the past' : '',
    isInvalid: !lower || !upper || isStartAfterEnd || isStartInPast,
  }
}

const ShiftExceptionEditor: FC<ShiftExceptionEditorProps> = props => {
  const { shiftException } = props
  const { lowerEmptyErrorText, upperEmptyErrorText, dateErrorText, pastTimeErrorText, isInvalid } =
    exceptionValidation(shiftException, !!props.overlapError)

  // An edit reports its row's validity with the change itself, so the parent sees both together.
  const reportRowError = (row: ShiftException) =>
    props.onError(exceptionValidation(row, !!props.overlapError).isInvalid)
  // The parent passes `onError` inline, so the effect reports through an event.
  const reportError = useEffectEvent((invalid: boolean) => props.onError(invalid))
  useEffect(() => {
    reportError(isInvalid)
  }, [shiftException, props.overlapError, isInvalid])

  return (
    <Box sx={{ marginTop: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box>
          <FFInput
            required
            error={!!dateErrorText || !!lowerEmptyErrorText || !!pastTimeErrorText}
            variant="outlined"
            onChange={event => {
              const updated = {
                ...shiftException,
                period: {
                  ...shiftException.period,
                  lower: shiftException.isAllDay
                    ? formatDate(event.target.value, true)
                    : formatDateTime(event.target.value),
                },
              }
              props.onChange(updated)
              reportRowError(updated)
            }}
            value={
              shiftException.isAllDay
                ? parseDate(shiftException.period.lower)
                : parseDateTime(shiftException.period.lower)
            }
            label={shiftException.isAllDay ? 'Starting' : 'Start On'}
            type={shiftException.isAllDay ? 'date' : 'datetime-local'}
            helperText={lowerEmptyErrorText || dateErrorText || pastTimeErrorText}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                inputProps: {
                  min: shiftException.isAllDay
                    ? new Date().toISOString().slice(0, 10)
                    : new Date().toISOString().slice(0, 16),
                  readOnly: props.isHistory,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ marginLeft: 2 }}>
          <FFInput
            required
            error={!!dateErrorText || !!upperEmptyErrorText}
            variant="outlined"
            onChange={event => {
              const updated = {
                ...shiftException,
                period: {
                  ...shiftException.period,
                  upper: shiftException.isAllDay
                    ? formatDate(event.target.value, false)
                    : formatDateTime(event.target.value),
                },
              }
              props.onChange(updated)
              reportRowError(updated)
            }}
            value={
              shiftException.isAllDay
                ? parseDate(shiftException.period.upper)
                : parseDateTime(shiftException.period.upper)
            }
            label={shiftException.isAllDay ? 'Through' : 'End On'}
            type={shiftException.isAllDay ? 'date' : 'datetime-local'}
            helperText={upperEmptyErrorText || dateErrorText}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                inputProps: {
                  min: shiftException.isAllDay
                    ? new Date().toISOString().slice(0, 10)
                    : new Date().toISOString().slice(0, 16),
                  readOnly: props.isHistory ? true : false,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ marginLeft: 2 }}>
          <Autocomplete<ShiftExceptionReason>
            size="small"
            options={SORTED_EXCEPTION_REASONS}
            value={shiftException.reason}
            renderInput={params => <TextField {...params} label="Reason" />}
            onChange={(_, selected) => {
              const updated = {
                ...shiftException,
                reason: selected,
              }
              props.onChange(updated)
              reportRowError(updated)
            }}
            style={{ width: 280 }}
            readOnly={props.isHistory ? true : false}
          />
        </Box>
        <Box sx={{ display: props.isHistory ? 'none' : 'block' }}>
          <IconButton
            onClick={() => props.onDelete(shiftException)}
            disabled={isShiftExceptionStartDateInPast(shiftException.period.lower)}
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
              },
              borderRadius: 'unset',
            }}
          >
            <DeleteIcon color="inherit" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Checkbox
          disabled={props.isHistory ? true : false}
          checked={shiftException.isAllDay || false}
          onChange={event => {
            const updated = {
              ...shiftException,
              isAllDay: event.target.checked,
            }
            props.onChange(updated)
            reportRowError(updated)
          }}
        />
        <Typography>All Day</Typography>
      </Box>
    </Box>
  )
}

export default ShiftExceptionEditor
